const actionsWrapperListenersAttached = new WeakSet();
const rowActionsListenersAttached = new WeakSet();
const bodyViewportActionsListenersAttached = new WeakSet();

const OVERLAY_SCROLLBAR_FALLBACK_WIDTH = 12;

export function rowHasVisibleActions(context, data) {
    if (!context?.hasActions || !data) return false;
    return context.rowHasActions?.(data) ?? true;
}

export function getBodyViewportScrollState(bodyViewport) {
    if (!bodyViewport) {
        return { hasScroll: false, scrollbarWidth: 0 };
    }

    const hasScroll = bodyViewport.scrollHeight > bodyViewport.clientHeight;
    if (!hasScroll) {
        return { hasScroll: false, scrollbarWidth: 0 };
    }

    const layoutScrollbarWidth = bodyViewport.offsetWidth - bodyViewport.clientWidth;

    return {
        hasScroll: true,
        // Overlay scrollbars (e.g. macOS) do not reduce clientWidth.
        scrollbarWidth:
            layoutScrollbarWidth > 0 ? layoutScrollbarWidth : OVERLAY_SCROLLBAR_FALLBACK_WIDTH,
    };
}

/** Subpixel tolerance when checking row ∩ body viewport containment. */
const VIEWPORT_CONTAINMENT_EPSILON = 1;

/**
 * Positions the floating row-actions overlay relative to the DataGrid root.
 * Returns null when the row is clipped by the body viewport so actions do not
 * float over empty space below/above the visible table body.
 */
export function getActionsWrapperPosition({ rowElement, dataGridRoot, rowHeight, bodyViewport }) {
    if (!rowElement || !dataGridRoot) return null;

    const rootRect = dataGridRoot.getBoundingClientRect();
    const rowRect = rowElement.getBoundingClientRect();
    const height = rowHeight > 0 ? rowHeight : rowRect.height;

    if (bodyViewport) {
        const viewportRect = bodyViewport.getBoundingClientRect();
        const rowBottom = rowRect.top + height;
        const isFullyVisible =
            rowRect.top >= viewportRect.top - VIEWPORT_CONTAINMENT_EPSILON &&
            rowBottom <= viewportRect.bottom + VIEWPORT_CONTAINMENT_EPSILON;

        if (!isFullyVisible) return null;
    }

    return {
        top: rowRect.top - rootRect.top,
        height,
    };
}

export function showActionsWrapper(actionsWrapper, { top, height, scrollbarWidth }) {
    if (!actionsWrapper) return;

    actionsWrapper.dataset.scrollbarWidth = String(scrollbarWidth || 0);
    actionsWrapper.style.display = 'block';
    actionsWrapper.style.pointerEvents = 'auto';
    actionsWrapper.style.top = `${top}px`;
    actionsWrapper.style.height = `${height}px`;
    applyStoredScrollbarOffset(actionsWrapper);
}

export function applyStoredScrollbarOffset(actionsWrapper) {
    if (!actionsWrapper) return;

    const scrollbarWidth = Number(actionsWrapper.dataset.scrollbarWidth || 0);
    actionsWrapper.style.right = scrollbarWidth > 0 ? `${scrollbarWidth}px` : '';
}

export function restoreActionsWrapperVisibility(actionsWrapper) {
    if (!actionsWrapper) return;

    actionsWrapper.style.display = 'block';
    actionsWrapper.style.pointerEvents = 'auto';
    applyStoredScrollbarOffset(actionsWrapper);
}

export function hideActionsWrapper(actionsWrapper) {
    if (!actionsWrapper) return;

    actionsWrapper.style.display = 'none';
    actionsWrapper.style.pointerEvents = 'none';
}

export function shouldSuppressRowHover(actionsWrapper) {
    return actionsWrapper?.dataset.floatingInteractionClosing === 'true';
}

export function shouldHideActionsOnPointerLeave(event, actionsWrapper) {
    const related = event.relatedTarget;
    if (!actionsWrapper) return true;
    if (actionsWrapper.dataset.floatingInteractionActive === 'true') return false;
    if (!related || !(related instanceof Node)) return true;

    return !actionsWrapper.contains(related);
}

/** @deprecated Use shouldHideActionsOnPointerLeave */
export function shouldHideActionsOnCellMouseOut(event, actionsWrapper) {
    return shouldHideActionsOnPointerLeave(event, actionsWrapper);
}

export function clearHoveredRowActions(context, actionsWrapper) {
    hideActionsWrapper(actionsWrapper);
    context?.getHoveredRowInfo?.(null);
    if (context?.possibleScrolledRow) {
        context.possibleScrolledRow = '';
    }
}

export function attachBodyViewportActionsListeners(bodyViewport, context, actionsWrapper) {
    if (!bodyViewport || bodyViewportActionsListenersAttached.has(bodyViewport)) return;
    bodyViewportActionsListenersAttached.add(bodyViewport);

    bodyViewport.addEventListener('mouseleave', (event) => {
        if (!shouldHideActionsOnPointerLeave(event, actionsWrapper)) return;
        clearHoveredRowActions(context, actionsWrapper);
    });
}

export function attachRowActionsListeners({
    rowElement,
    actionsWrapper,
    dataGridRoot,
    context,
    node,
    data,
    getBodyViewPort,
    onRowHover,
}) {
    if (!rowElement || rowActionsListenersAttached.has(rowElement)) return;
    rowActionsListenersAttached.add(rowElement);

    const bodyViewport = getBodyViewPort();
    attachBodyViewportActionsListeners(bodyViewport, context, actionsWrapper);

    const showForRow = () => {
        if (!bodyViewport) return;
        if (shouldSuppressRowHover(actionsWrapper)) return;

        const position = getActionsWrapperPosition({
            rowElement,
            dataGridRoot,
            rowHeight: node.rowHeight,
            bodyViewport,
        });

        if (!position || !rowHasVisibleActions(context, data)) {
            clearHoveredRowActions(context, actionsWrapper);
            return;
        }

        onRowHover?.(node.id);
        context.possibleScrolledRow = node.id;

        const { hasScroll, scrollbarWidth } = getBodyViewportScrollState(bodyViewport);
        context.getHoveredRowInfo({
            data,
            hasScroll,
            scrollbarWidth,
            rowElement,
        });

        if (actionsWrapper) {
            showActionsWrapper(actionsWrapper, {
                top: position.top,
                height: position.height,
                scrollbarWidth,
            });
            bodyViewport.onscroll = () => {
                clearHoveredRowActions(context, actionsWrapper);
            };
        }
    };

    rowElement.addEventListener('mouseenter', showForRow);
    rowElement.addEventListener('mouseleave', (event) => {
        if (!shouldHideActionsOnPointerLeave(event, actionsWrapper)) return;
        clearHoveredRowActions(context, actionsWrapper);
    });
}

export function attachActionsWrapperListeners(
    actionsWrapper,
    getBodyViewPort,
    context,
    addHoverClass,
) {
    if (!actionsWrapper || actionsWrapperListenersAttached.has(actionsWrapper)) return;
    actionsWrapperListenersAttached.add(actionsWrapper);

    actionsWrapper.addEventListener('mouseenter', () => {
        if (!actionsWrapper) return;
        addHoverClass(getBodyViewPort());
        restoreActionsWrapperVisibility(actionsWrapper);
    });

    actionsWrapper.addEventListener('mouseleave', (event) => {
        if (!actionsWrapper) return;
        if (!shouldHideActionsOnPointerLeave(event, actionsWrapper)) return;
        clearHoveredRowActions(context, actionsWrapper);
        actionsWrapper.scrollTo(0, 0);
    });

    const wheelEvent = 'onwheel' in actionsWrapper ? 'wheel' : 'mousewheel';
    actionsWrapper.addEventListener(
        wheelEvent,
        (event) => {
            if (!context?.possibleScrolledRow) return;
            getBodyViewPort()?.scrollBy(event.deltaX, event.deltaY);
            clearHoveredRowActions(context, actionsWrapper);
        },
        { passive: true },
    );
}

export function mergeColumnRowState(target, source) {
    let changed = false;

    Object.keys(source).forEach((column) => {
        const sourceColumn = source[column];
        if (!sourceColumn) return;

        if (!target[column]) {
            target[column] = {
                ...sourceColumn,
                rows: sourceColumn.rows ? { ...sourceColumn.rows } : undefined,
            };
            changed = true;
            return;
        }

        const targetColumn = target[column];

        ['areAllChecked', 'lazyAreAllChecked'].forEach((key) => {
            if (sourceColumn[key] !== undefined && targetColumn[key] !== sourceColumn[key]) {
                targetColumn[key] = sourceColumn[key];
                changed = true;
            }
        });

        const sourceRows = sourceColumn.rows || {};
        if (!targetColumn.rows) {
            if (Object.keys(sourceRows).length) {
                targetColumn.rows = { ...sourceRows };
                changed = true;
            }
            return;
        }

        Object.keys(sourceRows).forEach((rowId) => {
            if (targetColumn.rows[rowId] !== sourceRows[rowId]) {
                targetColumn.rows[rowId] = sourceRows[rowId];
                changed = true;
            }
        });
    });

    return changed;
}
